import { ArrowLeft } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@workspace/shadcn';
import { useData } from '@workspace/ui';

import { auth } from '../firebase.tsx';
import { MoyasarForm } from '../components/moyasar-form.tsx';
import { formatPrice } from '../lib/format.ts';

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('course') ?? '';
  const uid = auth.currentUser?.uid;

  const {
    data: course,
    isLoading,
    isError,
    refetch,
  } = useData('GET /courses/{slug}', { slug }, { retry: false, enabled: !!slug });

  const enrollment = useData(
    'GET /me/courses/{slug}',
    { slug },
    { retry: false, enabled: !!slug },
  );

  if (!slug || isError) {
    return (
      <Message>
        We could not find that course.{' '}
        <Link to="/" className="text-primary">
          Back to my courses
        </Link>
      </Message>
    );
  }

  if (isLoading || !course || enrollment.isPending) {
    return (
      <div className="mx-auto w-full max-w-2xl space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  // Money moves past this point, so the form renders only on the API's
  // explicit "not enrolled" answer — never because the ownership check failed.
  if (enrollment.isError) {
    return <CheckoutError />;
  }

  if (enrollment.data.enrollment) {
    return (
      <Message>
        You already own {course.title}.{' '}
        <Link to={`/courses/${course.slug}`} className="text-primary">
          Go to the course
        </Link>
      </Message>
    );
  }

  // A course with no price was never put on sale, and the API rejects any
  // charge against it, so there is nothing to render a form for.
  if (course.price === null) {
    return (
      <Message>
        {course.title} is not open for purchase yet.{' '}
        <Link to="/" className="text-primary">
          Back to my courses
        </Link>
      </Message>
    );
  }

  if (course.comingSoon) {
    return (
      <Message>
        {course.title} opens for enrollment once a cohort is scheduled.{' '}
        <Link to="/" className="text-primary">
          Back to my courses
        </Link>
      </Message>
    );
  }

  // The layout loader guarantees a signed-in learner, so this only guards the
  // gap before Firebase rehydrates the session.
  if (!uid) {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6">
      <Link
        to={`/courses/${course.slug}`}
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
      >
        <ArrowLeft className="size-4" />
        Back to course
      </Link>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-4">
            <span>{course.title}</span>
            <span className="text-primary text-2xl font-bold">
              {formatPrice(course.price)}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MoyasarForm
            amount={course.price}
            description={course.title}
            metadata={{ courseSlug: course.slug, uid }}
            onAmountStale={refetch}
          />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Covers a payment form that failed to initialise. Nothing on this page works
 * without it, so replacing the whole route is the honest outcome.
 */
export function CheckoutError() {
  return (
    <Message>
      We could not load the payment form. Please refresh the page, or{' '}
      <Link to="/" className="text-primary">
        go back to my courses
      </Link>
      .
    </Message>
  );
}

function Message({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-2xl">
      <p className="text-muted-foreground">{children}</p>
    </div>
  );
}
